import { nextTick } from 'vue';
import { modalController } from '@ionic/vue';
import PartnerPremiumUnlockModal from '@/components/partner/PartnerPremiumUnlockModal.vue';
import PartnerCheckoutModal from '@/components/partner/PartnerCheckoutModal.vue';
import HighlightBenefitsModal from '@/components/partner/HighlightBenefitsModal.vue';
import {
  HIGHLIGHT_ACTIVE_INFO_TITLE,
  getHighlightActiveInfoMessage,
} from '@/components/partner/partnerMembershipCopy.js';
import {
  pollPartnerSubscriptionCheckout,
  pollHighlightCheckout,
  pollPartnerCheckout,
} from '@/api/partner';
import store from '@/store';
import { isMysqlUtcAfterNow, parseMysqlUtcDatetime } from '@/utils/mysqlDatetime.js';

export const PARTNER_MEMBERSHIP_MODAL_CSS = 'event-participation-modal';

export const HIGHLIGHT_BENEFITS_MODAL_CSS = 'highlight-benefits-modal-shell';

const POLL_MAX_ATTEMPTS = 20;
const POLL_INTERVAL_MS = 1500;

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

export const isPartnerHighlightActive = (highlight) => {
  if (!highlight) return false;
  if (highlight.status === 'pending') return false;
  if (highlight.status === 'scheduled' || highlight.isScheduled) return false;
  if (highlight.isActive === true) return true;
  if (highlight.status === 'active') return isMysqlUtcAfterNow(highlight.endsAt);
  return isMysqlUtcAfterNow(highlight.endsAt);
};

export const isPartnerHighlightScheduled = (highlight) => (
  Boolean(highlight?.status === 'scheduled' || highlight?.isScheduled)
);

export const isPartnerHighlightPending = (highlight) => (
  Boolean(highlight?.status === 'pending')
);

export const formatPartnerHighlightRemaining = (endsAt, now = Date.now()) => {
  if (!endsAt) return '';
  const parsed = parseMysqlUtcDatetime(endsAt);
  if (!parsed) return '';
  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  const diffMs = parsed.getTime() - nowMs;
  if (diffMs <= 0) return 'läuft ab';
  const totalMinutes = Math.ceil(diffMs / (60 * 1000));
  if (totalMinutes < 60) {
    return `noch ${totalMinutes} Min.`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) {
    return minutes > 0 ? `noch ${hours} Std. ${minutes} Min.` : `noch ${hours} Std.`;
  }
  const days = Math.floor(hours / 24);
  return `noch ${days} Tag${days === 1 ? '' : 'e'}`;
};

export const mapHighlightPurchaseToClient = (purchase) => {
  if (!purchase) return null;
  return {
    id: purchase.id,
    productKey: purchase.productKey,
    startsAt: purchase.startsAt,
    endsAt: purchase.endsAt,
    source: purchase.source,
    status: purchase.status,
    isActive: purchase.status === 'active',
  };
};

export const extractProfilePartnerState = (status) => ({
  subscriptionActive: isPartnerPremiumActive(status),
  isDebugPartnerPremium: Boolean(status?.isDebugPartnerPremium),
  partnerPoints: Number(status?.points?.available || 0),
  pointsPending: Number(status?.points?.pending || 0),
  partnerHighlight: status?.highlight || status?.highlightPending || null,
});

export const isPartnerPremiumActive = (status) => (
  Boolean(status?.subscription?.isActive || status?.isDebugPartnerPremium)
);

export const buildPartnerStatusEventDetail = ({ status, purchase, points } = {}) => {
  if (status) {
    return {
      status,
      profile: extractProfilePartnerState(status),
    };
  }

  if (purchase) {
    const highlight = mapHighlightPurchaseToClient(purchase);
    const profile = { partnerHighlight: highlight };
    const statusPatch = {
      highlight,
      highlightPending: null,
    };
    if (points) {
      statusPatch.points = points;
      profile.partnerPoints = Number(points.available || 0);
      profile.pointsPending = Number(points.pending || 0);
    }
    return { statusPatch, profile };
  }

  return {};
};

export const notifyPartnerStatusChanged = (detail = {}) => {
  store.dispatch('partner/applyStatusUpdate', detail);
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('partner-status-changed', { detail }));
};

export const notifyPartnerSessionReset = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('partner-session-reset'));
};

export const presentPartnerModal = async (component, componentProps = {}, options = {}) => {
  const modal = await modalController.create({
    component,
    cssClass: options.cssClass || PARTNER_MEMBERSHIP_MODAL_CSS,
    componentProps,
    backdropDismiss: options.backdropDismiss !== false,
    showBackdrop: options.showBackdrop !== false,
  });
  const dismissPromise = modal.onDidDismiss();
  await modal.present();
  await nextTick();
  const { data, role } = await dismissPromise;
  return { data, role };
};

export const openPartnerPremiumUnlockModal = async (componentProps = {}) => presentPartnerModal(
  PartnerPremiumUnlockModal,
  componentProps,
);

export const openHighlightBenefitsModal = async (componentProps = {}) => presentPartnerModal(
  HighlightBenefitsModal,
  componentProps,
  {
    cssClass: HIGHLIGHT_BENEFITS_MODAL_CSS,
  },
);

export const showHighlightActiveInfoModal = async ({ remainingLabel, endsAtLabel } = {}) => {
  await openPartnerPremiumUnlockModal({
    title: HIGHLIGHT_ACTIVE_INFO_TITLE,
    message: getHighlightActiveInfoMessage(remainingLabel, endsAtLabel),
    primaryActionLabel: 'OK',
    secondaryActionLabel: '',
    confirmRole: 'ok',
  });
};

export const showPartnerCheckoutModal = async (variant, extra = {}) => {
  await presentPartnerModal(
    PartnerCheckoutModal,
    { variant, ...extra },
    { backdropDismiss: variant !== 'loading' },
  );
};

export const showPartnerErrorModal = async (title, message) => {
  await showPartnerCheckoutModal('error', { title, message, buttonLabel: 'OK' });
};

const pollWithLoadingModal = async (pollFn, isReady) => {
  const loadingModal = await modalController.create({
    component: PartnerCheckoutModal,
    componentProps: { variant: 'loading' },
    cssClass: PARTNER_MEMBERSHIP_MODAL_CSS,
    backdropDismiss: false,
  });
  await loadingModal.present();

  let result = null;
  for (let i = 0; i < POLL_MAX_ATTEMPTS; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      result = await pollFn();
      if (isReady(result)) {
        break;
      }
    } catch {
      result = null;
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_INTERVAL_MS);
  }

  await loadingModal.dismiss();
  return result;
};

export const pollUntilPartnerPackageReady = async (sessionId) => {
  const result = await pollWithLoadingModal(
    () => pollPartnerSubscriptionCheckout(sessionId),
    (pollResult) => Boolean(
      pollResult?.ready && pollResult?.status?.subscription?.isActive,
    ),
  );

  if (result?.ready && result?.status?.subscription?.isActive) {
    await showPartnerCheckoutModal('success');
    notifyPartnerStatusChanged(buildPartnerStatusEventDetail({ status: result.status }));
    return { ok: true, status: result.status };
  }

  await showPartnerCheckoutModal('error', {
    message: 'Die Aktivierung dauert länger als erwartet. Bitte prüfe deinen Status in ein paar Minuten erneut.',
  });
  return { ok: false, status: result?.status || null };
};

export const pollUntilHighlightReady = async (sessionId) => {
  const result = await pollWithLoadingModal(
    () => pollHighlightCheckout(sessionId),
    (pollResult) => Boolean(pollResult?.ready && pollResult?.purchase),
  );

  if (result?.ready && result?.purchase) {
    await showPartnerCheckoutModal('highlight_success');
    notifyPartnerStatusChanged(buildPartnerStatusEventDetail({ purchase: result.purchase }));
    return { ok: true, purchase: result.purchase };
  }

  await showPartnerCheckoutModal('error', {
    message: 'Die Highlight-Aktivierung dauert länger als erwartet. Bitte prüfe deinen Status in ein paar Minuten erneut.',
  });
  return { ok: false, purchase: null };
};

export const pollPartnerCheckoutReturn = async (sessionId) => {
  const result = await pollWithLoadingModal(
    () => pollPartnerCheckout(sessionId),
    (pollResult) => {
      if (!pollResult?.ready) return false;
      if (pollResult.purchaseType === 'highlight') return Boolean(pollResult.purchase);
      return Boolean(pollResult?.status?.subscription?.isActive);
    },
  );

  if (!result?.ready) {
    await showPartnerCheckoutModal('error', {
      message: 'Die Aktivierung dauert länger als erwartet. Bitte prüfe deinen Status in ein paar Minuten erneut.',
    });
    return { ok: false, purchaseType: result?.purchaseType || null };
  }

  if (result.purchaseType === 'highlight') {
    await showPartnerCheckoutModal('highlight_success');
    notifyPartnerStatusChanged(buildPartnerStatusEventDetail({ purchase: result.purchase }));
    return { ok: true, purchaseType: 'highlight', purchase: result.purchase };
  }

  await showPartnerCheckoutModal('success');
  notifyPartnerStatusChanged(buildPartnerStatusEventDetail({ status: result.status }));
  return { ok: true, purchaseType: 'partner_package', status: result.status };
};
